import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import './Dashboard.scss';

import StatusPanel from '../components/Dashboard/StatusPanel';
import MarketPanel from '../components/Dashboard/MarketPanel';
import AppFlowPanel from '../components/Dashboard/AppFlowPanel';
import StatsCards from '../components/Dashboard/StatsCards';

const Dashboard: React.FC = () => {
    const [mapOption, setMapOption] = useState<any>(null);
    const [activeRegion, setActiveRegion] = useState<string>('Chengde');
    const [isAutoRotatePaused, setIsAutoRotatePaused] = useState<boolean>(false);
    const chartRef = React.useRef<any>(null);
    const navigate = useNavigate();

    const mapPoints = [
        { name: 'Chengde', value: [117.9392, 40.9762], label: '承德' }, 
        { name: 'Panzhihua', value: [101.7186, 26.5823], label: '攀枝花' },
        { name: 'Shanghai', value: [121.4737, 31.2304], label: '上海' },
        { name: 'Hunan', value: [112.9388, 28.2282], label: '湖南' }
    ];

    const getSeriesData = (currentRegion: string) => {
        return mapPoints
            .filter(point => point.name === currentRegion)
            .map(point => {
                return {
                    name: point.name,
                    value: point.value,
                    itemStyle: { color: '#00e5ff' },
                    label: {
                        show: true,
                        formatter: point.label,
                        color: '#fff',
                        fontSize: 18,
                        fontWeight: 'bold',
                        backgroundColor: 'rgba(0, 229, 255, 0.2)',
                        borderColor: '#00e5ff',
                        borderWidth: 1,
                        padding: [6, 12],
                        borderRadius: 4,
                        offset: [0, -15]
                    },
                    symbol: 'pin',
                    symbolSize: 45
                };
            });
    };

    const handleMapClick = (params: any) => {
        if (params.componentType === 'series' && params.seriesType === 'effectScatter') {
            setActiveRegion(params.name);
            setIsAutoRotatePaused(true); 
        }
    };

    useEffect(() => {
        fetch('/assets/china.json')
            .then(response => response.json())
            .then(geoJson => {
                echarts.registerMap('china', geoJson);

                setMapOption({
                    backgroundColor: 'transparent',
                    geo: {
                        map: 'china',
                        roam: false, 
                        // 改为【等比缩放法则】
                        // 1. layoutSize: '80%' 确保无论屏幕多宽，地图的尺寸永远只占用最小边长的 80%，死守天然的长宽比例绝不变形
                        layoutSize: '80%', 
                        // 2. layoutCenter 定为水平正中间，垂直方向微下沉3%给顶部的数字面板让出完美的呼吸空间
                        layoutCenter: ['50%', '53%'], 
                        label: { show: false },
                        itemStyle: {
                            areaColor: 'rgba(28, 37, 46, 0.9)', 
                            borderColor: '#26a69a', 
                            borderWidth: 1.5,
                            shadowColor: 'rgba(0, 0, 0, 0.6)',
                            shadowOffsetX: 0,
                            shadowOffsetY: 15,
                            shadowBlur: 10
                        },
                        emphasis: {
                            itemStyle: { areaColor: '#37474f' },
                            label: { show: false }
                        }
                    },
                    series: [
                        {
                            type: 'effectScatter',
                            coordinateSystem: 'geo',
                            rippleEffect: { period: 4, scale: 3, brushType: 'stroke' },
                            itemStyle: { shadowBlur: 10, shadowColor: '#00e5ff' },
                            data: getSeriesData(activeRegion),
                            zlevel: 1
                        }
                    ]
                });
            })
            .catch(err => console.error("Failed to load map data", err));
    }, []);

    useEffect(() => {
        if (isAutoRotatePaused) return;
        const regions = ['Chengde', 'Panzhihua', 'Shanghai', 'Hunan'];
        const timer = setInterval(() => {
            setActiveRegion(prev => {
                const currentIndex = regions.indexOf(prev);
                const nextIndex = (currentIndex + 1) % regions.length;
                return regions[nextIndex];
            });
        }, 5000); 
        return () => clearInterval(timer);
    }, [isAutoRotatePaused]);

    useEffect(() => {
        if (!mapOption) return;
        setMapOption((prevOption: any) => {
            if (!prevOption) return null;
            return {
                ...prevOption,
                series: [{
                    ...prevOption.series[0],
                    data: getSeriesData(activeRegion)
                }]
            };
        });
    }, [activeRegion]);

    useEffect(() => {
        if (!chartRef.current) return;
        const chartInstance = chartRef.current.getEchartsInstance();
        const wrapper = chartInstance.getDom();
        const resizeObserver = new ResizeObserver(() => chartInstance.resize());
        if (wrapper) resizeObserver.observe(wrapper);
        return () => resizeObserver.disconnect();
    }, []);

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>钒荣网产业地图</h1>
                <nav>
                    <button onClick={() => navigate('/')}>首页</button>
                    <button onClick={() => navigate('/prices')}>价格</button>
                    <button onClick={() => navigate('/storage')}>储能</button>
                    <button onClick={() => navigate('/trade')}>贸易</button>
                </nav>
            </div>

            <div className="dashboard-main">
                <div className="map-layer">
                    {mapOption && (
                        <ReactECharts
                            ref={chartRef}
                            option={mapOption}
                            style={{ height: '100%', width: '100%' }}
                            onEvents={{ 'click': handleMapClick }}
                        />
                    )}
                </div>
                <StatsCards />
                <StatusPanel activeRegion={activeRegion} />
                <MarketPanel activeRegion={activeRegion} />
                <AppFlowPanel activeRegion={activeRegion} />
            </div>
        </div>
    );
};

export default Dashboard;
